import * as React from "react";
import {
  Bot,
  Download,
  FileText,
  Globe,
  Instagram,
  Mail,
  MessageCircle,
  MessageSquare,
  Phone,
  Send,
  Video,
  type LucideIcon,
} from "lucide-react";
import { cn } from "../lib/utils.js";

/**
 * Channel → icon + brand color mapping.
 * Colors follow design-proposal §2.2 channel colors — metadata only, never triage.
 * WHY shared: used in InboxLayout row, LeadDetailPage header, and bubbles —
 * 3+ sites means this belongs in @lumika/ui, not per-file.
 */
const CHANNEL_VISUAL: Record<
  string,
  { Icon: LucideIcon; colorClass: string; label: string }
> = {
  sms:       { Icon: MessageSquare, colorClass: "text-[#0E9F6E]", label: "SMS" },
  email:     { Icon: Mail,          colorClass: "text-[#2563EB]", label: "Email" },
  voice:     { Icon: Phone,         colorClass: "text-[#0F7A8C]", label: "Voice" },
  video:     { Icon: Video,         colorClass: "text-[#7C3AED]", label: "Video" },
  web:       { Icon: Globe,         colorClass: "text-[#7C3AED]", label: "Web chat" },
  web_form:  { Icon: FileText,      colorClass: "text-[#7C3AED]", label: "Web form" },
  messenger: { Icon: MessageCircle, colorClass: "text-[#1877F2]", label: "Messenger" },
  instagram: { Icon: Instagram,     colorClass: "text-[#E1306C]", label: "Instagram" },
  whatsapp:  { Icon: MessageCircle, colorClass: "text-[#25D366]", label: "WhatsApp" },
  telegram:  { Icon: Send,          colorClass: "text-sky-500",   label: "Telegram" },
  aap:       { Icon: Bot,           colorClass: "text-muted-foreground", label: "Auto Agent Protocol" },
  import:    { Icon: Download,      colorClass: "text-muted-foreground", label: "Imported" },
};

const FALLBACK = { Icon: MessageCircle, colorClass: "text-muted-foreground", label: "Unknown" };

export interface ChannelIconProps {
  channel: string;
  /** "sm" = 4×4 (16px), "xs" = 3×3 (12px, corner badge use). Default "sm". */
  size?: "xs" | "sm";
  className?: string;
}

/**
 * ChannelIcon — colored channel indicator.
 * Renders only the icon + color; no background circle so callers
 * decide the container (inline text, corner badge, etc.).
 */
export function ChannelIcon({ channel, size = "sm", className }: ChannelIconProps) {
  const v = CHANNEL_VISUAL[channel] ?? FALLBACK;
  const Icon = v.Icon;
  return (
    <Icon
      className={cn(
        size === "xs" ? "h-3 w-3" : "h-4 w-4",
        v.colorClass,
        className,
      )}
      aria-label={v.label}
      role="img"
    />
  );
}

/**
 * Returns the human label for a channel string.
 * Used by callers that need the text alongside the icon.
 */
export function channelLabel(channel: string): string {
  return (CHANNEL_VISUAL[channel] ?? FALLBACK).label;
}

export interface ChannelChipProps {
  channel: string;
  className?: string;
}

/**
 * ChannelChip — names the channel a reply goes out on (UIUX §2 "know what to
 * do immediately" rule). A QUIET neutral chip whose identity is carried by the
 * brand-COLORED icon, mirroring the channel connect-card treatment: on the now
 * hue-less neutral UI a filled brand pill was the loudest thing on screen, and
 * its teal collided with the primary teal buttons so a label read as a button.
 * The neutral surface + muted label + brand icon read correctly in both themes
 * (never text-primary here — in dark it is a fill, not a safe text color).
 */
export function ChannelChip({ channel, className }: ChannelChipProps) {
  const v = CHANNEL_VISUAL[channel] ?? FALLBACK;
  const Icon = v.Icon;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border bg-muted px-2 py-0.5 text-[11px] font-medium leading-normal text-muted-foreground",
        className,
      )}
      title={`Reply via ${v.label}`}
    >
      <Icon className={cn("h-3 w-3 shrink-0", v.colorClass)} aria-hidden />
      {v.label}
    </span>
  );
}
